import ReactGA from "react-ga4";

export const trackEvent = (category, action, label = "") => {
  const reVariant = getReVariantCookie();
  ReactGA.event({ category, action, label, ...(reVariant ? { re_variant: reVariant } : {}) });
};

const RE_VARIANT_COOKIE_NAME = "re_variant";
const RE_VARIANT_COOKIE_DAYS = 90; // ~3 months
const RE_VARIANT_COOKIE_DOMAIN = "electricshuffle.com";

// domain attribute is only valid on the production site, so on localhost/staging we fall back to a host-only cookie
const getReVariantCookieDomain = () =>
  window.location.hostname.includes(RE_VARIANT_COOKIE_DOMAIN) ? `.${RE_VARIANT_COOKIE_DOMAIN}` : "";

export const setCookie = (name, value, days) => {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  const domain = getReVariantCookieDomain();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/${domain ? `; domain=${domain}` : ""}`;
};

// Persists the Rapid Entry variant from the URL (UK only) so it can be attached to events across the main site
export const setReVariantCookieFromUrl = (search) => {
  const reVariant = new URLSearchParams(search).get(RE_VARIANT_COOKIE_NAME);
  if (!reVariant) return;
  setCookie(RE_VARIANT_COOKIE_NAME, reVariant, RE_VARIANT_COOKIE_DAYS);
};

export const getReVariantCookie = () => getCookie(RE_VARIANT_COOKIE_NAME);

const COOKIEYES_CONSENT_COOKIE = "cookieyes-consent";
const COOKIEYES_CONSENT_UPDATE_EVENT = "cookieyes_consent_update";

export const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

// CookieYes stores accepted categories as a comma separated "key:value" string, e.g. "necessary:yes,analytics:no"
const parseCookieyesConsent = () => {
  const raw = getCookie(COOKIEYES_CONSENT_COOKIE);
  if (!raw) return null;
  return raw.split(",").reduce((acc, pair) => {
    const [key, value] = pair.split(":");
    acc[key] = value === "yes";
    return acc;
  }, {});
};

// The CookieYes banner is currently only shown to UK and Ireland visitors, so consent gating only applies there
export const hasAnalyticsConsent = (country) => {
  if (country !== "uk") return true;
  return !!parseCookieyesConsent()?.analytics;
};

// Runs callback once analytics consent is present - immediately if already granted, otherwise on the CookieYes update event
export const onAnalyticsConsent = (country, callback) => {
  if (hasAnalyticsConsent(country)) {
    callback();
    return () => {};
  }

  const handleConsentUpdate = () => {
    if (!hasAnalyticsConsent(country)) return;
    callback();
    document.removeEventListener(COOKIEYES_CONSENT_UPDATE_EVENT, handleConsentUpdate);
  };

  document.addEventListener(COOKIEYES_CONSENT_UPDATE_EVENT, handleConsentUpdate);
  return () => document.removeEventListener(COOKIEYES_CONSENT_UPDATE_EVENT, handleConsentUpdate);
};

export const getBoundingClientRect = (element) => {
  const { top, right, bottom, left, width, height, x, y } = element.getBoundingClientRect();
  return { top, right, bottom, left, width, height, x, y };
};

//format date in hero
export const formatDate = (dateStr, suffixClassName) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const day = date.getDate();
  const suffix =
    day === 1 || day === 21 || day === 31
      ? "st"
      : day === 2 || day === 22
        ? "nd"
        : day === 3 || day === 23
          ? "rd"
          : "th";
  const month = date.toLocaleDateString("en-GB", { month: "long" });
  const year = date.getFullYear();
  return (
    <>
      {day}
      <span className={suffixClassName}>{suffix}</span> {month} {year}
    </>
  );
};

//function to find country from url
export const getEnvironment = () => {
  const href = window.location.href;

  if (href.includes("staging")) return "staging";
  if (href.includes("localhost")) return "development";
  return "production";
};

//for cookie yes banner
export const getCookieyesId = (environment) => {
  let cookieyesId;

  if (environment === "production") {
    cookieyesId = "fbbfc924951cf4fd3fd6c079";
  } else if (environment === "staging") {
    cookieyesId = "8c16dec01ef6c1a6ee1bc0e9";
  }

  return cookieyesId;
};

//capatlise uppercase txt
export const capitalize = (str) => str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
